import { RefObject } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'next-i18next';
import cn from 'classnames';
import { CSSProperties } from 'styled-components';

import {
  OmittedBaseDto,
  PickDtoFilter,
  TDtoWrapper,
  ValueType,
} from '@/shared/lib/forms/form-wrapper.types';
import { useSJDatabase } from '@/shared/lib/sj-orm/hooks/use-sj-database';
import { QuestionType } from '@/shared/lib/sj-orm/models/form/question.dto';
import { Checkbox } from '@/shared/uikitV2/Checkbox';
import { Option } from '@/shared/uikitV2/Select/types';
import { AllowedSelectEntityName } from '@/widgets/modals/containers/SelectEntitiesModal/types';

import { useFieldAction } from '../../useFieldAction';
import { getRequiredRule } from '../helpers';
import { DefaultInputToInputType, isDefaultInput } from '../types';

import { AddressField } from './AddressField';
import { AssetValueInput } from './AssetValueInput';
import { CoOwnersField } from './CoOwnersField';
import { DatePickerComponent } from './DatePickerComponent';
import { FileUploading, FileUploadingRef } from './FileUploading';
import { InputComponent } from './InputComponent';
import { FieldsArrayRef, LegalizedCopiesField } from './LegalizedCopiesField';
import { ALLOWED_PICK_ARRAY_ENTITIES, PickArrayOfDto } from './PickArrayOfDto';
import { PickOneOfDtos } from './PickOneOfDtos';
import { SelectFromList } from './SelectFromList';
import { SelectPidBeneficiaryV2 } from './SelectPidBeneficiaryV2';
import { ItemWrapper } from './styled';

export type RenderQuestionComponentProps<
  TDto extends OmittedBaseDto,
  THelp extends { [TKey in keyof TDto]: QuestionType },
> = {
  question: ValueType<QuestionType, TDtoWrapper<TDto, THelp>>;
  id: string;
  options?: Option[];
  label: string;
  uploaderRef?: RefObject<FileUploadingRef>;
  fieldsArrayRef?: RefObject<FieldsArrayRef>;
  disabled?: boolean;
  isHighlight?: boolean;
};

export const RenderQuestionComponent = <
  TDto extends OmittedBaseDto,
  THelp extends { [TKey in keyof TDto]: QuestionType },
>({
  question,
  label,
  id,
  options,
  uploaderRef,
  fieldsArrayRef,
  disabled,
  isHighlight,
}: RenderQuestionComponentProps<TDto, THelp>) => {
  const { t: tf } = useTranslation(['forms_assets', 'forms_personal_ids']);
  const sjDb = useSJDatabase();
  const { control, setValue, getValues } = useFormContext();
  const values = getValues();

  const handleFieldAction = useFieldAction({
    fieldName: id,
    setValue,
    fieldActionType: question.buttonAfter?.action,
  });

  const { required, colSpan } = question;

  if (isDefaultInput(question.type)) {
    const inputType = DefaultInputToInputType[question.type];

    return (
      <ItemWrapper style={{ '--grid-column': colSpan ? `span ${colSpan}` : '' } as CSSProperties}>
        <InputComponent
          inputType={inputType}
          name={id}
          question={question}
          label={tf(label)}
          required={required}
          id={id}
          key={id}
          deps={question.deps as Array<string>}
          collection={question.collection}
          disabled={disabled}
          isHighlight={isHighlight}
        />
      </ItemWrapper>
    );
  }

  switch (question.type) {
    case QuestionType.SELECT_FROM_LIST: {
      return (
        <SelectFromList
          name={id}
          control={control}
          options={options}
          label={tf(label)}
          required={getRequiredRule(question)}
          disabled={disabled}
          isMultiple={question.mode === 'multiple'}
          withSearch={question.withSearch}
        />
      );
    }

    case QuestionType.PICK_ARRAY_OF_DTO: {
      const collections = Array.isArray(question.items) ? question.items : [question.items];
      const createEntityTypes = Array.isArray(question.dtoType)
        ? question.dtoType
        : question.dtoType
        ? [question.dtoType]
        : undefined;
      const isMultiple = question.mode === 'multiple';
      const fieldName = id;
      const dtoFilter: PickDtoFilter | undefined =
        question.makeExtFilter?.({ fieldName, ctxEntity: values, sjdb: sjDb }) ?? question.filter;

      return (
        <ItemWrapper style={{ '--grid-column': colSpan ? `span ${colSpan}` : '' } as CSSProperties}>
          <PickArrayOfDto
            label={tf(label)}
            required={getRequiredRule(question)}
            collections={collections.filter((colName): colName is AllowedSelectEntityName =>
              ALLOWED_PICK_ARRAY_ENTITIES.some((name) => name === colName),
            )}
            createEntityTypes={createEntityTypes}
            createEntityCategories={question.dtoCategories}
            fieldName={fieldName}
            disabled={disabled}
            multiple={isMultiple}
            excludeIds={values.id ? [values.id] : undefined}
            filter={dtoFilter}
            isRequired={question.isRequired}
            onValueChange={question.onChange}
            key={id}
            isHighlight={isHighlight}
          />
        </ItemWrapper>
      );
    }

    case QuestionType.PICK_ONE_OF_DTOS: {
      const fieldName = id;
      const dtoFilter: PickDtoFilter | undefined =
        question.makeExtFilter?.({ fieldName, ctxEntity: values, sjdb: sjDb }) ?? question.filter;

      return (
        <ItemWrapper style={{ '--grid-column': colSpan ? `span ${colSpan}` : '' } as CSSProperties}>
          <PickOneOfDtos
            label={tf(label)}
            required={getRequiredRule(question)}
            fixedCollection={question.colName}
            dtoTypes={question.dtoType}
            fieldName={fieldName}
            disabled={disabled}
            excludeIds={values.id ? [values.id] : undefined}
            filter={dtoFilter}
            onValueChange={question.onChange}
            isRequired={question.isRequired}
            key={id}
          />
        </ItemWrapper>
      );
    }

    case QuestionType.SELECT_PID_BENEFICIARY: {
      return (
        <SelectPidBeneficiaryV2
          name={id}
          buttonAfterConfig={question.buttonAfter}
          buttonAfterLabel={tf(question?.buttonAfter?.label ?? '')}
          control={control}
          handleFieldAction={handleFieldAction}
          label={tf(label)}
          required={getRequiredRule(question)}
          disabled={disabled}
        />
      );
    }

    case QuestionType.CURRENCY_CALCULATOR:
    case QuestionType.ASSET_MARKET_VALUE_CALCULATOR: {
      return (
        <AssetValueInput
          label={tf(label)}
          required={getRequiredRule(question)}
          name={id}
          disabled={disabled}
          isHighlight={isHighlight}
        />
      );
    }

    case QuestionType.FILE_UPLOAD:
      return (
        <Controller
          name={id}
          control={control}
          rules={{ required: getRequiredRule(question) }}
          render={({ field }) => {
            return (
              <FileUploading
                {...field}
                label={tf(label)}
                name={id}
                acceptFiles={question.acceptFiles}
                allowedActions={question.allowedActions}
                disabled={disabled}
                uploaderRef={uploaderRef}
                setValue={setValue}
              />
            );
          }}
        />
      );

    case QuestionType.DATE_TIME:
      return (
        <Controller
          name={id}
          control={control}
          rules={{ required: getRequiredRule(question) }}
          render={({ field, fieldState }) => (
            <ItemWrapper>
              <DatePickerComponent
                {...field}
                disabled={disabled}
                placeholder={label}
                onChange={(...props) => field.onChange(props[0])}
                isDateRangePair={Boolean(question.isDateRangePair)}
                watchFieldName={question.watchFieldName ?? ''}
                className={cn('form-v2-date-picker', {
                  highlight: isHighlight && !fieldState.isDirty,
                })}
              />
            </ItemWrapper>
          )}
        />
      );

    case QuestionType.ADDRESS: {
      return (
        <ItemWrapper>
          <AddressField
            isHighlight={isHighlight}
            key={id}
            label={tf(label)}
            name={id}
            required={question.required}
          />
        </ItemWrapper>
      );
    }

    case QuestionType.CHECKBOX:
      return (
        <ItemWrapper
          style={
            {
              display: 'flex',
              '--grid-column': colSpan ? `span ${colSpan}` : '',
            } as CSSProperties
          }
        >
          <Controller
            name={id}
            control={control}
            rules={{ required: getRequiredRule(question) }}
            render={({ field: { value, ...field }, fieldState }) => (
              <Checkbox
                {...field}
                className={cn('form-v2-checkbox', {
                  highlight: isHighlight && !fieldState.isDirty,
                })}
                checked={value}
                label={tf(label)}
                disabled={disabled}
              />
            )}
          />
        </ItemWrapper>
      );

    case QuestionType.TEXT_MULTILINE: {
      return (
        <ItemWrapper style={{ '--grid-column': colSpan ? `span ${colSpan}` : '' } as CSSProperties}>
          <InputComponent
            inputType="text"
            name={id}
            question={question}
            label={tf(label)}
            required={required}
            id={id}
            key={id}
            deps={question.deps as Array<string>}
            collection={question.collection}
            disabled={disabled}
            isHighlight={isHighlight}
          />
        </ItemWrapper>
      );
    }

    case QuestionType.CO_OWNERS:
      return <CoOwnersField name={id} label={tf(label)} />;

    case QuestionType.LEGALIZED_COPIES:
      return <LegalizedCopiesField name={id} ref={fieldsArrayRef} />;

    default:
      return null;
  }
};
